import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Loader2 } from "lucide-react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Paper,
  Typography,
  Modal,
  CircularProgress,
} from "@mui/material";

// ---------------- HeaderPreview ----------------
export type HeaderPreviewProps = {
  title?: string;
  address?: string;
  phone?: string;
  phone2?: string;
  email?: string;
  website?: string;
  logoUrl?: string; // data URL or remote URL
  watermarkText?: string;
  PHC_REG_NO?: string;
};

// ---------------- Main Component ----------------
const LabPageHeader: React.FC = () => {
  const [title, setTitle] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [phone2, setPhone2] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [PHC_REG_NO, setPHC_REG_NO] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  //   const [logoBase64, setLogoBase64] = useState<string>(""); // base64 string WITHOUT data: prefix
  const [previewUrl, setPreviewUrl] = useState<string>(""); // data URL or remote URL
  const [loading, setLoading] = useState(false);
  //   const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [errors, setErrors] = useState({
    title: "",
    address: "",
    phone: "",
    logo: "",
  });
  const previewRef = useRef<HTMLDivElement | null>(null);

  const API_ROOT = process.env.REACT_APP_LAB_URL;

  // fetch header data
  const fetchHeaderData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.get(`${API_ROOT}/page-header/getPageHeader`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (resp?.data?.isSuccess) {
        const d = resp?.data?.data;
        setTitle(d.title || "");
        setPHC_REG_NO(d.PHC_REG_NO || "");
        setAddress(d.address || "");
        setPhone(d.phone || "");
        setPhone2(d.phone2 || "");
        setEmail(d.email || "");
        setWebsite(d.website || "");
        setPreviewUrl(d.logo);
      } else {
        // reset
        setTitle("");
        setPHC_REG_NO("");
        setAddress("");
        setPhone("");
        setPhone2("");
        setEmail("");
        setWebsite("");
        // setLogoBase64("");
        setPreviewUrl("");
      }
    } catch (err) {
      console.error(err);
      // keep graceful state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeaderData();
  }, []);

  // handle file selection -> validate size (<= 150KB) and convert to base64
  const onFileChange = (f: File | null) => {
    setErrors(e => ({ ...e, logo: "" }));
    if (!f) {
      setLogoFile(null);
      //   setLogoBase64("");
      setPreviewUrl("");
      return;
    }

    const maxBytes = 150 * 1024; // 150 KB
    if (f.size > maxBytes) {
      setErrors(e => ({ ...e, logo: "File too large (max 150 KB)" }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string; // data:<mime>;base64,AAAA
      // strip prefix
      //   const base64 = result.split(",")[1] ?? "";
      //   setLogoBase64(base64);
      setPreviewUrl(result);
      setLogoFile(f);
    };
    reader.readAsDataURL(f);
  };

  const validateForm = () => {
    const newErr = { title: "", address: "", phone: "", logo: "" };
    let ok = true;
    if (!title.trim()) {
      newErr.title = "Title is required";
      ok = false;
    }
    if (!address.trim()) {
      newErr.address = "Address is required";
      ok = false;
    }
    if (!phone.trim()) {
      newErr.phone = "Phone is required";
      ok = false;
    }
    // require either existing base64 or freshly uploaded file
    if (!previewUrl) {
      newErr.logo = "Logo is required (max 150 KB)";
      ok = false;
    }
    setErrors(newErr);
    return ok;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      // payload expects logo as base64 string (no data: prefix)
      const payload = {
        title: title,
        address: address,
        PHC_REG_NO: PHC_REG_NO,
        phone: phone,
        phone2: phone2,
        email: email,
        website: website,
        logo: previewUrl,
      };

      const res = await axios.put(
        `${API_ROOT}/page-header/setPageHeader`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Saved successfully");
      await fetchHeaderData();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Error saving data");
    } finally {
      setLoading(false);
    }
  };

  // delete — using removePageHeader endpoint (if your server uses a different path update here)
  const handleDelete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_ROOT}/page-header/removePageHeader`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // reset fields
      setTitle("");
      setPHC_REG_NO("");
      setAddress("");
      setPhone("");
      setPhone2("");
      setEmail("");
      setWebsite("");
      setLogoFile(null);
      //   setLogoBase64("");
      setPreviewUrl("");
      toast.success("Deleted successfully");
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Error deleting data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Grid container spacing={3}>
        {/* Form */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField
                label="Title"
                value={title}
                className="custom-input"
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px", // adjust as needed
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px", // fixes floating position
                  },
                }}
                onChange={e => setTitle(e.target.value)}
                fullWidth
                error={!!errors.title}
                helperText={errors.title}
              />
              <TextField
                label="Address"
                className="custom-input"
                value={address}
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px", // adjust as needed
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px", // fixes floating position
                  },
                }}
                onChange={e => setAddress(e.target.value)}
                fullWidth
                error={!!errors.address}
                helperText={errors.address}
              />
              <TextField
                label="Phone"
                className="custom-input"
                value={phone}
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px", // adjust as needed
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px", // fixes floating position
                  },
                }}
                onChange={e => setPhone(e.target.value)}
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone}
              />
              <TextField
                label="Phone 2 "
                className="custom-input"
                value={phone2}
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px", // adjust as needed
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px", // fixes floating position
                  },
                }}
                onChange={e => setPhone2(e.target.value)}
                fullWidth
              />

              <TextField
                label="Email address"
                className="custom-input"
                value={email}
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px", // adjust as needed
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px", // fixes floating position
                  },
                }}
                onChange={e => setEmail(e.target.value)}
                fullWidth
              />
              <TextField
                label="Website link"
                className="custom-input"
                value={website}
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px", // adjust as needed
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px", // fixes floating position
                  },
                }}
                onChange={e => setWebsite(e.target.value)}
                fullWidth
              />
              <TextField
                label="PHC REG NO"
                className="custom-input"
                value={PHC_REG_NO}
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "-10px", // adjust as needed
                  },
                  "& .MuiInputLabel-shrink": {
                    top: "0px", // fixes floating position
                  },
                }}
                onChange={e => setPHC_REG_NO(e.target.value)}
                fullWidth
              />

              <Box>
                {/* <Typography variant="subtitle2">Logo (max 150 KB)</Typography> */}
                <Button variant="contained" component="label" sx={{ mt: 1 }}>
                  {previewUrl ? "Change Logo" : "Upload Logo"}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={e => onFileChange(e.target.files?.[0] ?? null)}
                  />
                </Button>
                {errors.logo && (
                  <Typography color="error" variant="caption" display="block">
                    {errors.logo}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    Delete
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : null}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} md={8}>
          <Box ref={previewRef}>
            <HeaderPreview
              title={title || undefined}
              address={address || undefined}
              phone={phone || undefined}
              phone2={phone2 || undefined}
              email={email || undefined}
              website={website || undefined}
              logoUrl={previewUrl || undefined}
              PHC_REG_NO={PHC_REG_NO || undefined}
              watermarkText={"A4 Preview"}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LabPageHeader;

export const HeaderPreview: React.FC<HeaderPreviewProps> = ({
  title,
  address,
  phone,
  phone2,
  email,
  website,
  logoUrl,
  PHC_REG_NO,
  watermarkText = "A4 Preview",
}) => {
  return (
    <Paper
      elevation={1}
      sx={{ p: 3, position: "relative", overflow: "hidden", height: "66vh" }}
    >
      <Typography
        component="div"
        sx={{
          position: "absolute",
          inset: 0,
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          opacity: 0.06,
          fontWeight: 800,
          fontSize: 72,
          transform: "rotate(-30deg)",
          letterSpacing: 6,
          zIndex: 0,
        }}
      >
        {watermarkText}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left: Logo */}
        <Box
          sx={{
            width: 140,
            height: 140,
            display: "flex",
            alignItems: "start",
            justifyContent: "center",
          }}
        >
          {logoUrl ? (
            <Box
              component="img"
              src={
                logoUrl.startsWith("data:")
                  ? logoUrl
                  : `data:image/png;base64,${logoUrl}`
              }
              alt="logo"
              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "grey.100",
                border: "1px solid",
                borderColor: "grey.200",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "grey.400",
              }}
            >
              Logo
            </Box>
          )}
        </Box>

        {/* Right: Info */}
        <Box sx={{ flex: 1, textAlign: "center", position: "relative" }}>
          {/* Title */}

          <span className="absolute -top-5 right-24">
            {" "}
            PHC REG NO: {PHC_REG_NO}
          </span>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: "bold",
              lineHeight: 1.05,
              color: "#4d4a4aff",
              fontFamily: "'Georgia', serif",
            }}
          >
            {title || "Your Clinic / Lab Name"}
          </Typography>

          {/* Address */}

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              mt: 1,
              lineHeight: 0.7,
              fontSize: "1.5rem",
              fontFamily: "'Georgia', serif",
              color: "#4d4a4aff",
            }}
          >
            {address || "Address, City, Country"}
          </Typography>

          {/* Contact row */}
          <Typography variant="body2" sx={{ mt: 1 }}>
            {website && <span style={{ marginRight: "12px" }}>{website}</span>}
            {email && (
              <span style={{ marginRight: "12px" }}>E-Mail: {email}</span>
            )}
            {phone && <span style={{ marginRight: "12px" }}>{phone}</span>}
            {phone2 && <span>{phone2}</span>}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
